import { Link } from "react-router-dom";
import { Film, Mic, Send, ShieldCheck, Radio, ListPlus } from "lucide-react";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import Wordmark from "../components/Wordmark";
import { useAuth } from "../context/AuthContext";

const FILM_STEPS = [
  { Icon: Send, t: "Submit your film", d: "Tell us the title, synopsis and where people can watch it. Upload a poster." },
  { Icon: ShieldCheck, t: "We review it", d: "Every submission is reviewed by a person. Nothing goes live automatically." },
  { Icon: Radio, t: "It goes live", d: "Approved films appear in Indie Spotlight with a button that links straight to your page, plus a discussion space you host." },
];
const POD_STEPS = [
  { Icon: Send, t: "Submit your show", d: "Show name, host, a short description, cover art and your Spotify, Apple Podcasts or website link." },
  { Icon: ShieldCheck, t: "We review it", d: "Podcast Spotlight is for TV/film podcasts only. Shows outside that focus aren't approved." },
  { Icon: Radio, t: "It goes live", d: "Approved shows get a Podcast Spotlight spot and their own page with a link that opens your show." },
  { Icon: ListPlus, t: "Add episodes", d: "Linked podcasters can add episode links tagged to the films or shows they cover. After review, they appear under \u201cPodcasts talking about this\u201d on those title pages." },
];

function Track({ Icon, title, steps, cta, to, testid }) {
  return (
    <section className="mt-10 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-6 sm:p-8" data-testid={testid}>
      <h2 className="inline-flex items-center gap-2 font-head text-2xl text-[var(--text-primary)] sm:text-3xl"><Icon size={22} className="text-[var(--accent)]" /> {title}</h2>
      <ol className="mt-6 space-y-5">
        {steps.map(({ Icon: S, t, d }, i) => (
          <li key={t} className="flex gap-4">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent-hover)]"><S size={17} /></span>
            <div><p className="font-semibold text-[var(--text-primary)]">{i + 1}. {t}</p><p className="mt-0.5 text-sm leading-relaxed text-[var(--text-secondary)]">{d}</p></div>
          </li>
        ))}
      </ol>
      <Link to={to} className="mt-7 inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-6 py-3 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" data-testid={`${testid}-cta`}>{cta}</Link>
    </section>
  );
}

export default function ForCreators() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {user ? <Navbar /> : (
        <header className="sticky top-0 z-50 w-full border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
            <Link to="/" data-testid="creators-brand-logo"><Wordmark sizeClass="text-2xl sm:text-3xl" /></Link>
            <Link to="/login" className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Sign in</Link>
          </div>
        </header>
      )}
      <main className="mx-auto max-w-3xl px-4 pb-20 pt-8 sm:px-6" data-testid="creators-page">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--accent-hover)]">For Creators</p>
        <h1 className="mt-2 font-head text-4xl sm:text-5xl text-[var(--text-primary)]">How it works</h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">The Dial never hosts your work. We list it and link out to wherever you publish it, and you keep full ownership. Every submission is reviewed before it goes live, and nothing is sent to your email. Check back here or on the Home screen to see when you're live.</p>
        <Track Icon={Film} title="Indie Spotlight — for filmmakers" steps={FILM_STEPS} cta="Submit your film" to="/submit" testid="creators-film" />
        <Track Icon={Mic} title="Podcast Spotlight — for TV & film podcasters" steps={POD_STEPS} cta="Submit your podcast" to="/submit?tab=podcast" testid="creators-podcast" />
      </main>
      <Disclaimer />
    </div>
  );
}
